import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const BRAND_COLOR = [0, 83, 156];
const OUT_OF_RANGE_FILL = [253, 226, 226];

// Column layout of the exported PDF. Mirrors the on-screen search results grid
// minus the Actions column, which has no meaning on paper.
const PDF_COLUMNS = [
    { id: "id", label: "ID" },
    { id: "dateCollected", label: "Date Collected" },
    { id: "equipment", label: "Equipment" },
    { id: "currentTemperatureTop", label: "Current Temp (C)" },
    { id: "minTemperature", label: "Min (C)" },
    { id: "maxTemperature", label: "Max (C)" },
    { id: "isWithinRange", label: "In Range?" },
    { id: "samplesPresent", label: "Samples" },
    { id: "batteryLow", label: "Battery" },
    { id: "thermometerReset", label: "Reset" },
];

// Same Yes/No flags the grid shows beside In Range.
const BOOLEAN_COLUMNS = [
    "isWithinRange",
    "samplesPresent",
    "batteryLow",
    "thermometerReset",
];

const NUMERIC_COLUMNS = [
    "currentTemperatureTop",
    "minTemperature",
    "maxTemperature",
];

const formatDisplayDate = (d) => {
    if (!d) return "";
    const [y, m, day] = d.split("-");
    return `${m}/${day}/${y}`;
};

const formatCell = (columnId, record) => {
    const value = record[columnId];

    if (columnId === "dateCollected" && value) {
        return new Date(value).toLocaleString("en-US", {
            year: "numeric", month: "numeric", day: "numeric",
            hour: "numeric", minute: "2-digit",
        });
    }
    if (NUMERIC_COLUMNS.includes(columnId)) {
        return value != null && value !== "" && !isNaN(Number(value))
            ? Number(value).toFixed(1)
            : "";
    }
    if (BOOLEAN_COLUMNS.includes(columnId)) {
        return value == null ? "N/A" : value ? "Yes" : "No";
    }
    return value != null ? String(value) : "";
};

/**
 * Builds and downloads the Temperature Tracker search results as a PDF.
 * `records` is expected to already be filtered and sorted the same way the
 * grid shows them, so the paper copy matches what the user is looking at.
 */
export const generateTemperatureTrackerPdf = ({
    records,
    startDate,
    endDate,
    searchText,
    isSelection = false,
}) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginX = 30;

    // Header banner
    doc.setFillColor(...BRAND_COLOR);
    doc.rect(0, 0, pageWidth, 54, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(
        isSelection ? "Temperature Tracker Report (Selected Records)" : "Temperature Tracker Report",
        pageWidth / 2, 24, { align: "center" }
    );

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(
        `Date Collected: ${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`,
        pageWidth / 2, 40, { align: "center" }
    );

    doc.text(`Search: ${searchText || "None"}`, pageWidth / 2, 52, { align: "center" });

    const outOfRangeCount = records.filter((r) => r.isWithinRange === false).length;

    autoTable(doc, {
        startY: 70,
        margin: { left: marginX, right: marginX },
        head: [PDF_COLUMNS.map((c) => c.label)],
        body: records.map((r) => PDF_COLUMNS.map((c) => formatCell(c.id, r))),
        headStyles: { fillColor: BRAND_COLOR, textColor: 255, fontStyle: "bold", fontSize: 9 },
        bodyStyles: { fontSize: 8 },
        alternateRowStyles: { fillColor: [240, 246, 255] },
        // Out-of-spec readings are the whole point of the report, so flag them.
        didParseCell: (data) => {
            if (data.section !== "body") return;
            if (records[data.row.index]?.isWithinRange === false) {
                data.cell.styles.fillColor = OUT_OF_RANGE_FILL;
                data.cell.styles.textColor = [150, 0, 0];
            }
        },
        didDrawPage: () => {
            const pageHeight = doc.internal.pageSize.getHeight();
            doc.setFontSize(7);
            doc.setTextColor(120);
            doc.setFont("helvetica", "normal");
            doc.text(`Generated: ${new Date().toLocaleString()}`, marginX, pageHeight - 16);
            doc.text(
                `${records.length} record${records.length !== 1 ? "s" : ""} - ${outOfRangeCount} out of range`,
                pageWidth - marginX, pageHeight - 16, { align: "right" }
            );
        },
    });

    doc.save(`temperature-tracker-${startDate}-to-${endDate}.pdf`);
};

export default generateTemperatureTrackerPdf;
